import React from "react";
import {autorun} from "mobx";
import EsriFeature from "@arcgis/core/widgets/Feature";

import {PopupConfiguration} from "../../../../config/popup-configuration";
import {observer} from "mobx-react";
import {Button} from "semantic-ui-react";
import {ActionButtonConfiguration} from "../../../../config/action-configuration";
import {GeoICONS, Icon} from "../../../icon/icon";
import {PopupPanelActionOea} from "../popup-panel-actions/popup-panel-action-oea";
import * as arcade from "@arcgis/core/arcade";
import EsriGraphic from "@arcgis/core/Graphic";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {PathUtils} from "../../../../../utils/path-utils";
import {IAppStore, withStore} from "../../../../stores/abstract-app-store";

interface PopupItemProps {
    config: PopupConfiguration;
    graphic: __esri.Graphic;
    featureIndex: number;
    panelIndex: number;
    store?: IAppStore; // this is injected by withStore
    titleHandle: (newTitle: string) => void;
    onHighlightClick: (e: React.MouseEvent, panelIndex: number, featureIndex: number) => void;
    onZoomInClick: (e: React.MouseEvent, panelIndex: number, featureIndex: number) => void;
}
@observer
class PopupPanelItemBase extends React.Component<PopupItemProps, any> {
    watchHandles: any[] = [];
    popupItemRef: any;
    constructor(props: PopupItemProps) {
        super(props);
        this.popupItemRef = React.createRef();
        this.state = {
            title: false,
            profileIsLoading: false
        };
        this.handleButtonClick = this.handleButtonClick.bind(this);
    }
    componentDidMount(): void {
        const {featureIndex, panelIndex, graphic, store} = this.props;
        autorun((reaction: any) => {
            if (!store?.mapStore.view) {
                return;
            }
            const popupStore = store.PopupStore;

            const featureChild = new EsriFeature({
                container: this.popupItemRef.current,
                graphic,
                map: store.mapStore.map,
                defaultPopupTemplateEnabled: true,
                spatialReference: store.mapStore.view.spatialReference,
                visibleElements: { title: false }
            });
            this.watchHandles.push(featureChild.watch("title", (title) => {
                this.setState({title});
                if(title && popupStore.getActivePage(panelIndex) === featureIndex){
                    this.props.titleHandle(featureChild.title);
                }
            }));
            this.watchHandles.push(featureChild.viewModel.watch("waitingForContent", (waitingForContent) => {
                if (!waitingForContent) {
                    if (featureChild.title !== this.state.title) {
                        this.setState({title: featureChild.title});
                        if(popupStore.getActivePage(panelIndex) === featureIndex) {
                            this.props.titleHandle(featureChild.title);
                        }
                    }
                    // wird für eine allfällige Neupositionierung des Popups verwendet
                    popupStore.setLastItemTitle(featureChild.title);
                }
            }));
            reaction.dispose();
        });
    }
    componentDidUpdate() {
        const {store, panelIndex} = this.props;
        const popupStore = store?.PopupStore;

        if (popupStore?.getActivePage(panelIndex) === this.props.featureIndex) {
            if(this.state.title){
                this.props.titleHandle(this.state.title);
            }
        }
    }
    componentWillUnmount(): void {
        const { graphic, store} = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        // allfälliges Timeout von "ExternalService" löschen
        store?.PopupStore.clearTimeout((graphic as any).sourceLayer.uid+graphic.getObjectId());
    }
    handleButtonClick(e: React.MouseEvent, action: ActionButtonConfiguration): void {
        const {graphic, store} = this.props;
        if (!store?.mapStore.view) {
            return;
        }
        if (typeof action.id !== "undefined") {
            if (action.id === "gravis") {
                window.open(action.url +
                    "Suche.Gemeinde="
                    + (graphic.attributes.BFS_NR? graphic.attributes.BFS_NR: graphic.attributes.bfs_nr)
                    + "&FOCUS=" + store.mapStore.view.center.x + ":" + store.mapStore.view.center.y
                    + ":" + store.mapStore.view.scale,
                "gravis");
            } else if (action.id === "oereb") {
                const egrid: string =
                    (graphic.attributes.EGRIS_EGRID? graphic.attributes.EGRIS_EGRID: graphic.attributes.egris_egrid)
                window.open((action.url as string).replace("{EGRID}", egrid),
                "oereb");
            } else if (action.id === "objekt") {
                window.open(action.url + ""
                    + (graphic.attributes.EGRIS_EGRID? graphic.attributes.EGRIS_EGRID: graphic.attributes.egris_egrid),
                "objekt");
            } else if (action.id === "link") {
                const profile: __esri.Profile = {
                    variables: [{
                        name: "$feature",
                        type: "feature"
                    }]
                };
                if(action.url) {
                    const actionUrl = action.url?.indexOf("`") > -1 ? action.url : "`" + action.url + "`";
                    const arcadeExecutor = arcade.createArcadeExecutor(actionUrl, profile);
                    arcadeExecutor.then((ae) => {
                        const link = ae.execute({
                            $feature: graphic
                        });
                        window.open(link, "link");
                    });
                } else {
                    // eslint-disable-next-line no-console
                    console.warn("Keine URL definiert!");
                }
            } else if (action.id === "profile") {
                store?.uiStore.setActiveTool("profile", graphic);
            } else if (action.id === "profile_query") {
                const queryConfig = action.query;
                if(queryConfig) {
                    this.setState({profileIsLoading: true});
                    let searchLayer: FeatureLayer | __esri.Sublayer | __esri.MapImageLayer | undefined;
                    // sourceLayer on Graphic is not documented!
                    if ("sourceLayer" in graphic){
                        searchLayer = (graphic as any).sourceLayer;
                        if(searchLayer && searchLayer.hasOwnProperty("layer") && searchLayer.hasOwnProperty("id")
                            && queryConfig.hasOwnProperty("layerId") && queryConfig.layerId !== searchLayer?.id){
                            searchLayer = (searchLayer as __esri.Sublayer).layer as __esri.MapImageLayer;
                        }
                    } else if (queryConfig.id === "self"
                        && queryConfig.hasOwnProperty("portalItem")) {
                        const layerConfig = {
                            layerType: "feature",
                            layerId: (queryConfig.layerId) ? queryConfig.layerId : undefined,
                            portalItem: queryConfig.portalItem,
                            opacity: 1,
                            listMode: "hide",
                            legendEnabled: false,
                            title: (queryConfig.title) ? queryConfig.title : undefined
                        };
                        searchLayer = new FeatureLayer(layerConfig as __esri.FeatureLayerProperties);
                    }else{
                        // eslint-disable-next-line no-console
                        console.warn("Keine gültiger Layer für Query definiert!");
                    }
                    if(searchLayer) {
                        searchLayer.load().then((v) => {
                            if(searchLayer && searchLayer.hasOwnProperty("allSublayers")) {
                                searchLayer = v.allSublayers.find((sublayer: __esri.Sublayer) =>
                                    sublayer.id === queryConfig.layerId);
                            }
                            if (searchLayer) {
                                const profile: __esri.Profile = {
                                    variables: [{
                                        name: "$feature",
                                        type: "feature"
                                    }]
                                };
                                const arcadeExecutor = arcade.createArcadeExecutor(queryConfig.expression, profile);
                                arcadeExecutor.then((ae) => {
                                    let where = ae.execute({
                                        $feature: graphic
                                    });
                                    if (typeof where === "string" && searchLayer) {
                                        // definitionExpression in Suche berücksichtigen, falls diese definiert ist.
                                        if ((searchLayer as any).definitionExpression) {
                                            where = `(${where}) AND (${(searchLayer as any).definitionExpression})`;
                                        }
                                        const query = (searchLayer as any).createQuery();
                                        query.where = where;
                                        (searchLayer as any).queryFeatures(query)
                                            .then((response: __esri.FeatureSet) => {
                                                if (response.features.length === 0) {
                                                    // eslint-disable-next-line no-console
                                                    console.warn("Kein Profil Resultat gefunden");
                                                    return;
                                                }
                                                let geometry: __esri.Geometry;
                                                if (response.features.length > 1) {
                                                    const geometries: __esri.Geometry[] = [];
                                                    response.features.forEach((item) => {
                                                        geometries.push(item.geometry);
                                                    });
                                                    geometry = geometryEngine.union(geometries);
                                                } else {
                                                    geometry = response.features[0].geometry;
                                                }
                                                const featureGraphic = new EsriGraphic({
                                                    geometry,
                                                    symbol: store?.appConfig.search.resultSymbol?.line
                                                });
                                                store?.uiStore.setActiveTool("profile", featureGraphic);
                                                this.setState({profileIsLoading: false});
                                                if (store.mapStore.view) {
                                                    store.mapStore.view.graphics.push(
                                                        featureGraphic
                                                    );
                                                    const fullExtent = featureGraphic.geometry.extent.clone();
                                                    fullExtent.expand(1.5);
                                                    store.mapStore.view?.goTo(fullExtent).catch((error) => {
                                                        if (error.name !== "view:goto-interrupted") {
                                                            // eslint-disable-next-line no-console
                                                            console.error(error);
                                                        }
                                                    });
                                                }
                                            });
                                    }
                                });
                            }
                        });
                    }
                } else {
                    // eslint-disable-next-line no-console
                    console.warn("Keine Query definiert!");
                }
            } else {
                // TODO: Bei nächsten API Update überprüfen ob es wieder mit der ESRI trigger-action funktioniert
                // popup.popup.viewModel.triggerAction(popup.actionMap[action.id]);
                const event = new CustomEvent("geo-trigger-action", {detail: action});
                window.dispatchEvent(event);
            }
        }
    }
    render(): React.ReactElement | null {
        const { title } = this.state;
        const {
            config,
            graphic,
            featureIndex,
            panelIndex,
            store,
            onHighlightClick,
            onZoomInClick
        } = this.props;

        if (!store || !store.PopupStore) {
            return null;
        }
        const popupStore = store.PopupStore;

        const activePage = popupStore.getActivePage(panelIndex);
        const visible = (activePage === featureIndex);
        let contentClass = "geo-feature";
        if (popupStore.selectedPanelIndex === panelIndex && visible) {
            contentClass += " geo-feature-selected";
        }
        if (visible) {
            contentClass += " geo-feature-show";
        } else {
            contentClass += " geo-feature-hide";
        }
        const buttonsTop: React.ReactElement[] = [];
        const buttons: React.ReactElement[] = [];
        if (title !== false) {
            let buttonCount = 0;
            // Zoom Button bei Imagery Layer ausblenden (diese besitzen das Attribut "Raster.ServicePixelValue")
            if(typeof graphic.getAttribute("Raster.ServicePixelValue") === "undefined") {
                if (config.dockEnabled) {
                    buttonsTop.push(
                        <Button
                            compact
                            icon
                            size="mini"
                            key={"button-" + buttonCount}
                            onClick={(e: React.MouseEvent) =>
                                onZoomInClick(e, panelIndex, featureIndex)
                            }>
                            <Icon name={"zoom_in"}/>
                        </Button>
                    );
                } else {
                    buttons.push(
                        <Button
                            compact
                            size="mini"
                            key={"button-" + buttonCount}
                            onClick={(e: React.MouseEvent) =>
                                onZoomInClick(e, panelIndex, featureIndex)
                            }>
                            <Icon name={"zoom_in"}/> Zoomen
                        </Button>
                    );
                }
                buttonCount++;
            }
            // sourceLayer on Graphic is not documented!
            const source = (graphic as any).sourceLayer;
            let popupTemplate: __esri.PopupTemplate | null = null;
            if (source) {
                popupTemplate = (source as __esri.Sublayer).popupTemplate;
                // parsedUrl on Layer is not documented!
                let url = (source as any).parsedUrl?.path;
                if (!url) {
                    url = source.url; // MapImageLayer Service
                }
                if (typeof url === "string") {
                    let metauid = PathUtils.getMetaId(url);
                    const layerId = PathUtils.getLayerId(url);
                    if(metauid){
                        metauid = PathUtils.slug(metauid);
                        contentClass+=" "+metauid;
                        if (typeof layerId === "number"){
                            contentClass+=" "+metauid+"-"+layerId;
                        }
                    }
                }
            }
            if (popupTemplate && popupTemplate.actions) {
                popupTemplate.actions.forEach((actionConfig) => {
                    if(actionConfig.id==="oea"){
                        buttons.push(
                            <PopupPanelActionOea
                                key={`button-${buttonCount}`}
                                size="mini"
                                actionConfig={actionConfig as ActionButtonConfiguration}
                                graphic={graphic}>
                            </PopupPanelActionOea>
                        );
                    } else {
                        let buttonCss = actionConfig.className;
                        if (actionConfig.id==="profile_query" && this.state.profileIsLoading) {
                            buttonCss += " geo-profile-loading";
                        }
                        buttons.push(
                            <Button
                                compact
                                size="mini"
                                key={"button-" + buttonCount}
                                className={buttonCss}
                                onClick={(e: React.MouseEvent) => {
                                    this.handleButtonClick(e, actionConfig as ActionButtonConfiguration);
                                }} >
                                {actionConfig.icon &&
                                    <Icon name={actionConfig.icon as GeoICONS}/>
                                }
                                {actionConfig.id === "profile_query" &&
                                    <Icon name={"recycle"} className={"geo-rotating"}/>} {actionConfig.title
                                }
                            </Button>
                        );
                    }
                    buttonCount++;
                });
            }
        }
        return (
            <div className={contentClass}
                onClick={(e: React.MouseEvent) => onHighlightClick(e, panelIndex, featureIndex)}
                onMouseEnter={(e: React.MouseEvent) => onHighlightClick(e, panelIndex, featureIndex)} >
                {buttonsTop.length>0 &&
                    <div className="geo-action-buttons geo-action-buttons-top">
                        {buttonsTop}
                    </div>
                }
                <div ref={this.popupItemRef}/>
                {buttons.length>0 &&
                    <div className="geo-action-buttons geo-action-buttons-bottom">
                        {buttons}
                    </div>
                }
            </div>
        );
    }
}

export const PopupPanelItem = withStore(PopupPanelItemBase);
